import { t } from 'i18next';
import { isEmpty, throttle } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocationPostsQuery } from '../../../components/api/queries/useLocationsQuery';
import useUserQuery from '../../../components/api/queries/useUserQuery';
import AddUserProfileV1BottomSheet from '../../../components/bottomSheet/AddUserProfileV1BottomSheet';
import SecondaryButton from '../../../components/buttons/SecondaryButton';
import PostPreview from '../../../components/community/PostPreview';
import InfiniteScroll from '../../../components/InfiniteScroll';
import ProfileAvatar from '../../../components/thumbnails/ProfileAvatar';
import { ReactComponent as EditIcon } from '../../../icons/edit.svg';
import { IPostsResponseBody } from '../../../types/response/IPostsResponseBody';
import { openNewTab } from '../../../utils/UIUtils';

interface EventPostsProps {
  locationId: string;
  eventId?: string;
}

const EventPosts: React.FC<EventPostsProps> = ({ locationId, eventId }) => {
  const queryClient = useQueryClient();

  const [posts, setPosts] = useState<IPostsResponseBody[]>([]);
  const [isOpenAddProfileBottomSheet, setIsOpenAddProfileBottomSheet] = useState(false);

  const user = useUserQuery();
  const {
    data: PostsData,
    isFetching,
    fetchNextPage,
    hasNextPage
  } = useLocationPostsQuery({
    locationId: locationId,
    eventId: eventId,
    perPage: 5
  });

  useEffect(() => {
    if (PostsData) {
      const data = PostsData.pages?.flatMap((page) =>
        page.posts.map((post: IPostsResponseBody) => post)
      );
      setPosts(data);
    }
  }, [PostsData]);

  useEffect(() => {
    const handlePostCreationComplete = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || isEmpty(event.data?.type)) return;

      if (event.data.type === 'post-creation-complete') {
        const post = event.data.params?.post;
        if (post) {
          queryClient.invalidateQueries('LocationPosts');
        }
      }
    };

    const throttledReceiveMessage = throttle(handlePostCreationComplete, 1000);
    window.addEventListener('message', throttledReceiveMessage);

    return () => {
      window.removeEventListener('message', throttledReceiveMessage);
    };
  }, [queryClient]);

  const navigateToAddPost = (locationId: string, eventId?: string) => {
    if (user?.baseUserProfile) {
      openNewTab(`/posts/add/?locationId=${locationId}&eventId=${eventId}&category=FreeTalk`);
    } else {
      setIsOpenAddProfileBottomSheet(true);
    }
  };

  return (
    <>
      <span className="text-[15px] mx-[20px] font-bold text-main-light-2 dark:text-main-dark-2">
        {t('OfflineEventDetail.postsTitle')}
      </span>
      {/* TODO: Handle to empty user?.baseUserProfile */}
      <div onClick={() => navigateToAddPost(locationId, eventId)}>
        <div className="mt-[16px] px-[22px] pb-[20px] border-b-[1px] border-separator-light-1 dark:border-separator-dark-2">
          <div className="flex flex-row">
            <ProfileAvatar
              profileImageUrl={user?.baseUserProfile?.thumbnailUrl}
              widthAndHeight={'34px'}
              onClick={null}
            />
            <div className="flex flex-col ml-[10px]">
              <div className="font-semibold leading-[17px] text-[14px] text-main-light-2 dark:text-main-dark-2">
                {user?.baseUserProfile?.nickname}
              </div>
              <div className="mt-[5px] text-[15px] leading-[19px] text-sub-light-1 dark:text-sub-dark-1">
                {t('OfflineEventDetail.postsPlaceholder')}
              </div>
              <div className="mt-[8px] max-w-fit">
                <SecondaryButton
                  text={t('OfflineEventDetail.writePost')}
                  disabled={false}
                  leftIcon={
                    <EditIcon className="w-[15px] h-[15px] fill-primary-light dark:fill-primary-dark pr-[2px]" />
                  }
                  paddingX={'18px'}
                  paddingY={'11px'}
                  icon={null}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {!isEmpty(posts) && (
        <InfiniteScroll
          items={posts}
          loadMore={fetchNextPage}
          isFetching={isFetching}
          hasNextPage={hasNextPage}
          renderItem={(post: IPostsResponseBody) => (
            <div
              className="border-b-[1px] border-separator-light-1 dark:border-separator-dark-1"
              key={post.postId}
              style={{
                padding: '20px 22px'
              }}
            >
              <PostPreview
                user={{ baseUserProfile: { userProfileId: post.userProfileId } }}
                post={post}
                navigatePath={''}
                disableActions={true}
                isEvent={!!eventId}
              />
            </div>
          )}
        />
      )}
      {isOpenAddProfileBottomSheet && (
        <AddUserProfileV1BottomSheet
          title={t('createFeedUserProfilePage.title')}
          userProfile={undefined}
          isShowProfileBottomSheet={isOpenAddProfileBottomSheet}
          setIsShowProfileBottomSheet={setIsOpenAddProfileBottomSheet}
          category={'base'}
          onCompleted={() => setIsOpenAddProfileBottomSheet(false)}
        />
      )}
    </>
  );
};

export default EventPosts;
